import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Button, Alert } from 'reactstrap';

const ImportQuizModal = ({ isOpen, toggle, onSubmit, isSubmitting }) => {
  const [formData, setFormData] = useState({
    excelFile: null,
    thumbnail: null,
    name: '',
    displayQuestions: 5,
    isActive: true,
    timeLimit: 60,
    defaultThumbnailUrl: ''
  });
  
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        excelFile: null,
        thumbnail: null,
        name: '',
        displayQuestions: 5,
        isActive: true,
        timeLimit: 60,
        defaultThumbnailUrl: ''
      });
      setPreviewUrl(null);
      setErrors({});
    }
  }, [isOpen]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate Excel file
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        setErrors({ ...errors, excelFile: 'Vui lòng chọn file Excel (.xlsx hoặc .xls)' });
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setErrors({ ...errors, excelFile: 'File size không được vượt quá 10MB' });
        return;
      }
      
      setFormData({ ...formData, excelFile: file });
      setErrors({ ...errors, excelFile: null });
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors({ ...errors, thumbnail: 'Vui lòng chọn file hình ảnh' });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, thumbnail: 'File size không được vượt quá 5MB' });
        return;
      }

      setFormData({ ...formData, thumbnail: file });
      setErrors({ ...errors, thumbnail: null });

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    const newErrors = {};
    if (!formData.excelFile) {
      newErrors.excelFile = 'Vui lòng chọn file Excel';
    }
    if (!formData.name.trim()) {
      newErrors.name = 'Vui lòng nhập tên quiz';
    }
    if (formData.displayQuestions < 1) {
      newErrors.displayQuestions = 'Số câu hỏi hiển thị phải lớn hơn 0';
    }
    if (formData.timeLimit < 1) {
      newErrors.timeLimit = 'Thời gian giới hạn phải lớn hơn 0';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      keyboard={!isSubmitting}
    >
      <ModalHeader toggle={!isSubmitting ? toggle : undefined}>
        <i className="fa fa-file-excel me-2 text-success"></i>
        Import Quiz từ Excel
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Alert color="info" className="mb-4">
            <i className="fa fa-info-circle me-2"></i>
            <strong>Hướng dẫn:</strong>
            <ul className="mb-0 mt-2">
              <li>Upload file Excel chứa câu hỏi và đáp án</li>
              <li>File Excel phải có định dạng .xlsx hoặc .xls</li>
              <li>Thumbnail là tùy chọn, có thể để trống</li>
              <li>Hệ thống sẽ tự động đọc và tạo quiz từ file Excel</li>
            </ul>
          </Alert>

          <FormGroup>
            <Label>File Excel *</Label>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelFileChange}
              required
            />
            {formData.excelFile && (
              <small className="text-success d-block mt-1">
                <i className="fa fa-check-circle me-1"></i>
                Đã chọn: {formData.excelFile.name}
              </small>
            )}
            {errors.excelFile && (
              <small className="text-danger d-block mt-1">
                <i className="fa fa-exclamation-circle me-1"></i>
                {errors.excelFile}
              </small>
            )}
            <small className="text-muted d-block mt-1">Max 10MB. File Excel (.xlsx, .xls)</small>
          </FormGroup>

          <FormGroup>
            <Label>Tên Quiz Template *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Quiz Test từ Excel"
              required
            />
            {errors.name && (
              <small className="text-danger d-block mt-1">
                <i className="fa fa-exclamation-circle me-1"></i>
                {errors.name}
              </small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Thumbnail (Tùy chọn)</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
            />
            {formData.thumbnail && (
              <small className="text-success d-block mt-1">
                <i className="fa fa-check-circle me-1"></i>
                Đã chọn: {formData.thumbnail.name}
              </small>
            )}
            {errors.thumbnail && (
              <small className="text-danger d-block mt-1">
                <i className="fa fa-exclamation-circle me-1"></i>
                {errors.thumbnail}
              </small>
            )}
            <small className="text-muted d-block mt-1">Max 5MB. JPG, PNG, GIF, WebP</small>
          </FormGroup>

          {previewUrl && (
            <FormGroup>
              <Label>Preview Thumbnail</Label>
              <div className="border rounded p-2">
                <img 
                  src={previewUrl} 
                  alt="Thumbnail preview" 
                  className="img-fluid rounded"
                  style={{maxHeight: '200px', width: '100%', objectFit: 'contain'}}
                />
              </div>
            </FormGroup>
          )}

          <FormGroup>
            <Label>Default Thumbnail URL (Tùy chọn)</Label>
            <Input
              type="url"
              value={formData.defaultThumbnailUrl}
              onChange={(e) => setFormData({ ...formData, defaultThumbnailUrl: e.target.value })}
              placeholder="https://example.com/default-thumbnail.jpg"
            />
            <small className="text-muted d-block mt-1">URL hình ảnh mặc định nếu không upload thumbnail</small>
          </FormGroup>

          <FormGroup>
            <Label>Số câu hỏi hiển thị *</Label>
            <Input
              type="number"
              value={formData.displayQuestions}
              onChange={(e) => setFormData({ ...formData, displayQuestions: parseInt(e.target.value) || 5 })}
              min="1"
              required
            />
            {errors.displayQuestions && (
              <small className="text-danger d-block mt-1">
                <i className="fa fa-exclamation-circle me-1"></i>
                {errors.displayQuestions}
              </small>
            )}
            <small className="text-muted d-block mt-1">Random từ tổng số câu hỏi trong Excel</small>
          </FormGroup>

          <FormGroup>
            <Label>Thời gian giới hạn (giây) *</Label>
            <Input
              type="number"
              value={formData.timeLimit}
              onChange={(e) => setFormData({ ...formData, timeLimit: parseInt(e.target.value) || 60 })}
              min="1"
              required
            />
            {errors.timeLimit && (
              <small className="text-danger d-block mt-1">
                <i className="fa fa-exclamation-circle me-1"></i>
                {errors.timeLimit}
              </small>
            )}
            <small className="text-muted d-block mt-1">Thời gian mặc định cho mỗi câu hỏi</small>
          </FormGroup>

          <FormGroup>
            <div className="form-check form-switch">
              <Input
                type="checkbox"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <Label className="form-check-label">
                {formData.isActive ? 'Active' : 'Inactive'}
              </Label>
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button color="success" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Đang import...
              </>
            ) : (
              <>
                <i className="fa fa-file-excel me-2"></i>
                Import Quiz
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ImportQuizModal;

